// Phase 2 V7 (Apr 19 2026) — Modal to create a custom voice.
// V8 (Apr 19 evening) — UX polish: visible step indicator, clearer CTAs,
// play button with prominent label, toast success, and now uses the
// parent's create+refresh instead of its own hook instance (avoids the
// state-not-shared bug where the modal created a voice that the picker
// didn't see until remount).
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Pause, Play, Sparkles, X } from "lucide-react";
import toast from "react-hot-toast";
import { previewCustomVoice } from "@/services/ads/videos/customVoiceService";
import type {
  CreateCustomVoiceRequest,
  CustomVoiceResponse,
  PreviewCustomVoiceResponse,
} from "@/services/ads/videos/videoDraft.types";
import { useAuth } from "@/context/AuthContext";
import { dictionary, type Language } from "@/utils/dictionary";

const MAX_DESCRIPTION = 500;
const MAX_NAME = 50;

interface CustomVoiceCreatorProps {
  open: boolean;
  onClose: () => void;
  /** The parent's create() + refresh() — keeps the VoiceLibrary list in sync. */
  createVoice: (payload: CreateCustomVoiceRequest) => Promise<CustomVoiceResponse | null>;
  onVoiceCreated?: (voice: CustomVoiceResponse) => void;
}

type Stage = "describing" | "generating" | "previewing" | "saving";

export function CustomVoiceCreator({ open, onClose, createVoice, onVoiceCreated }: CustomVoiceCreatorProps) {
  const { language } = useAuth();
  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] ?? fallback;

  const [stage, setStage] = useState<Stage>("describing");
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState<PreviewCustomVoiceResponse | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setStage("describing");
      setDescription("");
      setPreview(null);
      setDisplayName("");
      setErrorMsg(null);
      setPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    }
  }, [open]);

  if (!open) return null;

  const stepNum = stage === "describing" ? 1 : stage === "generating" ? 2 : stage === "previewing" ? 2 : 3;

  const handleGenerate = async () => {
    const trimmed = description.trim();
    if (!trimmed) {
      setErrorMsg(t("custom-voice-error-empty", "Escribe cómo querés que suene."));
      return;
    }
    setErrorMsg(null);
    setStage("generating");
    const { res, error } = await previewCustomVoice({ description: trimmed });
    if (!res) {
      const trig = (error as { trigger?: string } | null)?.trigger;
      setErrorMsg(
        trig === "TTS_SAFETY_BLOCK"
          ? t(
              "custom-voice-error-safety",
              "Esta descripción fue bloqueada por el filtro. Probá con palabras más neutras (ej: cálida, alegre, confiada).",
            )
          : t("custom-voice-error-generic", "No pudimos generar el preview. Intentalo de nuevo."),
      );
      setStage("describing");
      return;
    }
    setPreview(res);
    setDisplayName(res.suggested_display_name);
    setStage("previewing");
    // Auto-play the preview on arrival so the user doesn't have to click.
    setTimeout(() => {
      if (!audioRef.current) {
        audioRef.current = new Audio(res.sample_url);
        audioRef.current.addEventListener("ended", () => setPlaying(false));
      }
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {
        // Autoplay blocked in some browsers — user can still click play.
      });
    }, 200);
  };

  const handleTogglePlay = () => {
    if (!preview) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(preview.sample_url);
      audioRef.current.addEventListener("ended", () => setPlaying(false));
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const handleRestart = () => {
    setPreview(null);
    setDisplayName("");
    setErrorMsg(null);
    setPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setStage("describing");
  };

  const handleSave = async () => {
    if (!preview) return;
    const name = displayName.trim();
    if (!name) {
      setErrorMsg(t("custom-voice-error-name", "Ponele un nombre a tu voz."));
      return;
    }
    setErrorMsg(null);
    setStage("saving");
    const saved = await createVoice({
      display_name: name,
      description: description.trim(),
      base_voice: preview.base_voice,
      style_prompt: preview.style_prompt,
      sample_url: preview.sample_url,
    });
    if (!saved) {
      setErrorMsg(t("custom-voice-error-save", "No pudimos guardar la voz."));
      setStage("previewing");
      return;
    }
    toast.success(
      t("custom-voice-saved-toast", `"${name}" agregada a tu biblioteca`),
    );
    onVoiceCreated?.(saved);
    onClose();
  };

  const isBusy = stage === "generating" || stage === "saving";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-fluxi-nested-modal>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={isBusy ? undefined : onClose} />
      <div className="relative bg-background rounded-2xl shadow-2xl border border-border/50 w-[min(95vw,580px)] max-h-[90vh] overflow-y-auto">
        {/* Header with step indicator */}
        <div className="px-6 pt-5 pb-4 border-b border-border/50">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">
                {t("custom-voice-title", "Crea tu voz")}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40"
              aria-label={t("custom-voice-close", "Cerrar")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Visual step indicator */}
          <div className="flex items-center gap-2">
            <StepDot num={1} label={t("custom-voice-step-1", "Describí")} active={stepNum === 1} done={stepNum > 1} />
            <StepLine done={stepNum > 1} />
            <StepDot num={2} label={t("custom-voice-step-2", "Escuchá")} active={stepNum === 2} done={stepNum > 2} />
            <StepLine done={stepNum > 2} />
            <StepDot num={3} label={t("custom-voice-step-3", "Guardá")} active={stepNum === 3} done={false} />
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Step 1: describing */}
          {stage === "describing" && (
            <>
              <div>
                <label className="text-[12px] font-semibold text-foreground">
                  {t("custom-voice-desc-label", "Describe cómo querés que suene la voz")}
                </label>
                <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
                  {t("custom-voice-desc-hint", "1 o 2 líneas. Acento + personalidad + edad ayudan.")}
                </p>
                <textarea
                  autoFocus
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
                  placeholder={t(
                    "custom-voice-desc-placeholder",
                    "Ej: Voz femenina paisa de 25 años, alegre y cercana tipo influencer de maquillaje.",
                  )}
                  rows={4}
                  className="w-full rounded-xl border border-border/70 bg-white p-3 text-sm resize-none focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20"
                  maxLength={MAX_DESCRIPTION}
                />
                <div className="flex justify-end mt-1 text-[11px] text-muted-foreground">
                  <span>{description.length}/{MAX_DESCRIPTION}</span>
                </div>
              </div>
              {errorMsg && (
                <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {errorMsg}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  {t("custom-voice-cancel", "Cancelar")}
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={!description.trim()}
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("custom-voice-generate", "Generar preview")}
                </button>
              </div>
            </>
          )}

          {/* Step 2a: generating */}
          {stage === "generating" && (
            <div className="py-10 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-sm font-semibold text-foreground">
                {t("custom-voice-generating", "Creando tu voz…")}
              </p>
              <p className="text-[12px] text-muted-foreground max-w-xs text-center leading-relaxed">
                {t(
                  "custom-voice-generating-hint",
                  "Analizamos tu descripción y rendereamos un preview. Toma unos 10 segundos.",
                )}
              </p>
            </div>
          )}

          {/* Step 2b: previewing */}
          {stage === "previewing" && preview && (
            <>
              <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.06] to-accent/[0.04] p-5 space-y-4">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={handleTogglePlay}
                    className="flex-shrink-0 w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:opacity-90 shadow-md transition-transform hover:scale-105"
                    aria-label={playing ? t("custom-voice-pause", "Pausar") : t("custom-voice-play", "Reproducir")}
                  >
                    {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-primary uppercase tracking-wider mb-1">
                      {t("custom-voice-preview-label", "Escuchá tu voz")}
                    </p>
                    <p className="text-[13px] text-foreground leading-snug">
                      {preview.reasoning}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[12px] font-semibold text-foreground">
                  {t("custom-voice-name-label", "Ponele un nombre para guardarla")}
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, MAX_NAME))}
                  className="mt-2 w-full rounded-xl border border-border/70 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/20"
                  placeholder={t("custom-voice-name-placeholder", "Ej: Influencer Paisa")}
                  maxLength={MAX_NAME}
                />
              </div>

              {errorMsg && (
                <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {errorMsg}
                </div>
              )}

              <div className="flex justify-between gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleRestart}
                  className="px-4 py-2 rounded-full text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  {t("custom-voice-restart", "Empezar de nuevo")}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!displayName.trim()}
                  className="px-5 py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-sm"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t("custom-voice-save", "Guardar y usar")}
                </button>
              </div>
            </>
          )}

          {/* Step 3: saving */}
          {stage === "saving" && (
            <div className="py-10 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-sm font-semibold text-foreground">
                {t("custom-voice-saving", "Guardando…")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepDot({ num, label, active, done }: { num: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <div
        className={[
          "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors",
          done ? "bg-green-500 text-white" :
          active ? "bg-primary text-primary-foreground" :
          "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : num}
      </div>
      <span
        className={[
          "text-[11px] font-medium",
          active ? "text-foreground" : "text-muted-foreground",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

function StepLine({ done }: { done: boolean }) {
  return (
    <div
      className={[
        "flex-1 h-[2px] rounded-full transition-colors",
        done ? "bg-green-500" : "bg-muted",
      ].join(" ")}
    />
  );
}
